/**
 * UI Constants - Centralized magic numbers
 *
 * This file contains all hardcoded values used throughout the application.
 * Extracting these values makes the codebase more maintainable and prevents
 * accidental breaking changes.
 *
 * ⚠️ CRITICAL: Many of these values are coordinated between components.
 * Always check documentation before changing.
 */

// ============================================================================
// MODAL SYSTEM CONSTANTS
// ============================================================================
// 📚 See: docs/SYSTEMS/MODAL_IMAGE_PRIORITY_SYSTEM.md

/**
 * Modal maximum height in pixels
 *
 * CRITICAL: This value is coordinated with:
 * - MODAL_BLUR_OVERLAY_BOTTOM in CSS
 * - GRID_SCROLL_OFFSET in NewHomePage.js
 *
 * @see src/components/StickerModal.css line 22
 * @constant {number}
 */
export const MODAL_MAX_HEIGHT = 610;

/**
 * Extra spacing below modal for grid visibility
 *
 * @constant {number}
 */
export const MODAL_SPACING = 50;

/**
 * Total grid scroll offset (MODAL_MAX_HEIGHT + MODAL_SPACING)
 *
 * CRITICAL: Used in NewHomePage.js for scroll positioning
 * Must equal MODAL_MAX_HEIGHT + MODAL_SPACING
 *
 * @see src/components/NewHomePage.js line 556
 * @constant {number}
 */
export const GRID_SCROLL_OFFSET = MODAL_MAX_HEIGHT + MODAL_SPACING; // 660px

/**
 * CSS calc value for modal blur overlay bottom position
 *
 * @see src/components/StickerModal.css line 7
 * @constant {string}
 */
export const MODAL_BLUR_OVERLAY_BOTTOM = `calc(100vh - ${MODAL_MAX_HEIGHT}px)`;

// ============================================================================
// IMAGE OPTIMIZATION CONSTANTS
// ============================================================================
// 📚 See: docs/ESSENTIAL/RECENT_OPTIMIZATIONS.md

/**
 * Etsy image size identifier for optimized loading
 *
 * CRITICAL: Using il_570xN instead of il_fullxfull provides:
 * - 10-25x faster loading (from 30+ seconds to 1-2 seconds)
 * - ~200KB vs 2-5MB file size
 * - Minimal quality loss for modal display
 *
 * @see src/components/CoreStickerCard.js line 292
 * @constant {string}
 */
export const ETSY_IMAGE_SIZE_OPTIMIZED = 'il_570xN';

/**
 * Etsy full-resolution image size identifier (NOT USED - too slow)
 *
 * @deprecated Use ETSY_IMAGE_SIZE_OPTIMIZED instead
 * @constant {string}
 */
export const ETSY_IMAGE_SIZE_FULL = 'il_fullxfull';

// ============================================================================
// DIMENSION CONVERSION CONSTANTS
// ============================================================================

/**
 * Pixels per inch for dimension conversion
 *
 * Used to convert sticker dimensions from inches to pixels
 * @constant {number}
 */
export const PIXELS_PER_INCH = 96;

/**
 * Padding added to sticker dimensions in pixels
 *
 * @constant {number}
 */
export const STICKER_DIMENSION_PADDING = 48;

// ============================================================================
// Z-INDEX LAYERS
// ============================================================================

/**
 * Z-index values for layering UI elements
 *
 * Use these constants to maintain consistent stacking order
 */
export const Z_INDEX = {
  BASE: 1,
  STICKER: 2,
  STICKER_HOVER: 3,
  STICKER_ACTIVE: 4,
  HERO_BACKGROUND: 9,
  HERO_FRAME_FRONT: 12,
  HERO_FRAME_MACHINE: 14,
  ERROR_OVERLAY: 9999,
  ADMIN_OVERLAY: 10000,
};

// ============================================================================
// TIMEOUT CONSTANTS
// ============================================================================

/**
 * Common timeout values in milliseconds
 */
export const TIMEOUTS = {
  DEBOUNCE_SHORT: 100,   // Quick debounce for search/input
  DEBOUNCE_MEDIUM: 300,  // Standard debounce
  DEBOUNCE_LONG: 500,    // Long debounce for expensive operations
  TOAST_DISPLAY: 3000,   // How long to show toast notifications
  ANIMATION_SHORT: 200,  // Quick animations
  ANIMATION_MEDIUM: 300, // Standard animations
  ANIMATION_LONG: 500,   // Longer animations
};

// ============================================================================
// SPACING & SIZING CONSTANTS
// ============================================================================

/**
 * Common spacing values in pixels
 */
export const SPACING = {
  XS: 4,
  SM: 8,
  MD: 12,
  LG: 16,
  XL: 24,
  XXL: 32,
};

/**
 * Border radius values for consistent rounded corners
 */
export const BORDER_RADIUS = {
  SM: 4,
  MD: 6,
  LG: 8,
  XL: 16,
  PILL: 999, // For pill-shaped buttons
};

/**
 * Common border widths
 */
export const BORDER_WIDTH = {
  THIN: 1,
  MEDIUM: 2,
  THICK: 3,
};

// ============================================================================
// BREAKPOINTS FOR RESPONSIVE DESIGN
// ============================================================================

/**
 * Responsive breakpoints in pixels
 *
 * Mobile-first approach: min-width for larger screens
 */
export const BREAKPOINTS = {
  MOBILE: 480,
  TABLET: 768,
  DESKTOP: 1024,
  WIDE: 1440,
};

/**
 * Media query strings for easy use in styled components
 */
export const MEDIA_QUERIES = {
  mobile: `@media (max-width: ${BREAKPOINTS.MOBILE}px)`,
  tablet: `@media (min-width: ${BREAKPOINTS.MOBILE + 1}px) and (max-width: ${BREAKPOINTS.TABLET}px)`,
  desktop: `@media (min-width: ${BREAKPOINTS.TABLET + 1}px)`,
  wide: `@media (min-width: ${BREAKPOINTS.WIDE}px)`,
};

// ============================================================================
// GRID & LAYOUT CONSTANTS
// ============================================================================

/**
 * Virtual grid configuration
 */
export const GRID = {
  CARD_HEIGHT: 400,     // Approximate card height for virtual scrolling
  CARD_WIDTH: 300,      // Approximate card width
  GAP: 16,              // Gap between grid items
  COLUMNS_MOBILE: 1,
  COLUMNS_TABLET: 2,
  COLUMNS_DESKTOP: 3,
  COLUMNS_WIDE: 4,
};

// ============================================================================
// LOADING & PERFORMANCE CONSTANTS
// ============================================================================

/**
 * Performance and loading thresholds
 */
export const PERFORMANCE = {
  MAX_CONCURRENT_IMAGES: 6,      // Max images to load simultaneously
  INTERSECTION_THRESHOLD: 0.1,    // Intersection observer threshold
  INFINITE_SCROLL_THRESHOLD: 0.8, // When to trigger infinite scroll (80% scrolled)
  PAGE_SIZE: 50,                  // Items per page for pagination
};

// ============================================================================
// VALIDATION CONSTANTS
// ============================================================================

/**
 * Input validation limits
 */
export const VALIDATION = {
  MIN_SEARCH_LENGTH: 2,
  MAX_SEARCH_LENGTH: 100,
  MIN_TITLE_LENGTH: 3,
  MAX_TITLE_LENGTH: 200,
  MAX_FILE_SIZE_MB: 10,
};

// ============================================================================
// EXPORTS FOR BACKWARD COMPATIBILITY
// ============================================================================

/**
 * Export all constants as a single object for convenience
 */
export default {
  // Modal System
  MODAL_MAX_HEIGHT,
  MODAL_SPACING,
  GRID_SCROLL_OFFSET,
  MODAL_BLUR_OVERLAY_BOTTOM,

  // Images
  ETSY_IMAGE_SIZE_OPTIMIZED,
  ETSY_IMAGE_SIZE_FULL,

  // Dimensions
  PIXELS_PER_INCH,
  STICKER_DIMENSION_PADDING,

  // Layering
  Z_INDEX,

  // Timing
  TIMEOUTS,

  // Spacing
  SPACING,
  BORDER_RADIUS,
  BORDER_WIDTH,

  // Responsive
  BREAKPOINTS,
  MEDIA_QUERIES,

  // Grid
  GRID,

  // Performance
  PERFORMANCE,

  // Validation
  VALIDATION,
};
